import { create } from "zustand";
import {
  getUserConfig,
  changeColorTheme,
  setBotAvatar,
  uploadUserAvatar,
  changeMessageTheme,
  setDomainFocus as persistDomainFocus,
  setResponseContext as apiSetResponseContext,
  getResponseContextMenu,
} from "../axios/user";
import { playActionSound } from "../helpers/actionSound";
import { clearMessageMenuCache } from "../helpers/messageMenuCache";
import useChatStore from "./chatStore";
import { PersonaMenuItem, ResponseContextMenuResponse, ResponseContextState } from "@/types/realtimeSpeech";
import {
  buildResponseContextState,
  isResponseContextState,
} from "../utils/responseContextUtils";
import { hasAuthToken } from "../utils/authToken";

const MESSAGE_THEME_STORAGE_KEY = "messageTheme";
const APP_CONFIG_CACHE_KEY = "coachmate-app-config-cache";
const VALID_THEMES = ["both", "lessons", "chats", "mmk"] as const;

type CachedAppConfig = {
  focus: string;
  domain: string;
  colorTheme: "light" | "dark";
  botAvatar: "Alice" | "Endy" | "Mario" | "Robbi" | "Fatima" | "";
  userAvatar: string;
  messageTheme: "both" | "lessons" | "chats" | "mmk";
  showAttachmentsButton: boolean;
};

const readCachedAppConfig = (): CachedAppConfig | null => {
  try {
    if (!hasAuthToken()) return null;
    const stored = localStorage.getItem(APP_CONFIG_CACHE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as CachedAppConfig;
    return parsed?.domain || parsed?.focus ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedAppConfig = (config: CachedAppConfig) => {
  try {
    localStorage.setItem(APP_CONFIG_CACHE_KEY, JSON.stringify(config));
  } catch {
    // ignore
  }
};

const patchCachedDomainFocus = (
  domain: string,
  focus: string,
  state: Pick<
    CachedAppConfig,
    | "colorTheme"
    | "botAvatar"
    | "userAvatar"
    | "messageTheme"
    | "showAttachmentsButton"
  >,
) => {
  const cached = readCachedAppConfig();
  if (cached) {
    writeCachedAppConfig({ ...cached, domain, focus });
    return;
  }

  writeCachedAppConfig({
    domain,
    focus,
    colorTheme: state.colorTheme,
    botAvatar: state.botAvatar,
    userAvatar: state.userAvatar,
    messageTheme: state.messageTheme,
    showAttachmentsButton: state.showAttachmentsButton,
  });
};

const getStoredMessageTheme = (): "both" | "lessons" | "chats" | "mmk" => {
  try {
    const stored = localStorage.getItem(MESSAGE_THEME_STORAGE_KEY);
    if (stored && (VALID_THEMES as readonly string[]).includes(stored)) {
      return stored as "both" | "lessons" | "chats" | "mmk";
    }
  } catch {
    // ignore
  }
  return "both";
};

interface AppStore {
  showAttachmentsButton: boolean;
  focus: string;
  domain: string;
  originalFocus: string | null;
  originalDomain: string | null;
  markedIds: Record<string, string>;
  colorTheme: "light" | "dark";
  messageTheme: "both" | "lessons" | "chats" | "mmk";
  fetchConfig: () => Promise<void>;
  updateColorTheme: (newTheme: "light" | "dark") => Promise<void>;
  updateMessageTheme: (
    newMessage: "both" | "lessons" | "chats" | "mmk",
  ) => Promise<string>;
  isDark: boolean;
  setIsDark: (value: boolean) => void;
  setDomainFocus: (
    domain: string,
    focus: string,
    storeOriginal?: boolean,
  ) => void;
  restoreOriginalDomainFocus: () => void;
  setFocus: (focus: string) => void;
  setDomain: (domain: string) => void;
  botAvatar: "Alice" | "Endy" | "Mario" | "Robbi" | "Fatima" | "";
  updateBotAvatar: (
    newAvatar: "Alice" | "Endy" | "Mario" | "Robbi" | "Fatima" | "",
  ) => Promise<void>;
  userAvatar: string;
  uploadUserAvatar: (file: File) => Promise<void>;
  isBrowserLocked: boolean;
  browserLockedMessage: string | null;
  setIsBrowserLocked: (value: boolean, playSound?: boolean) => Promise<void>;
  setBrowserLockedMessage: (message: string | null) => void;
  isAudioLoading: boolean;
  setIsAudioLoading: (value: boolean) => void;

  shouldHideStopMessage: boolean;
  setShouldHideStopMessage: (value: boolean) => void;

  sellingDialogue: boolean;
  setSellingDialogue: (value: boolean) => void;

  asyncRoleplayOpen: boolean;
  setAsyncRoleplayOpen: (value: boolean) => void;
  asyncRoleplaySessionId: string | null;
  setAsyncRoleplaySessionId: (value: string | null) => void;
  asyncRoleplayRecordId: string | null;
  setAsyncRoleplayRecordId: (value: string | null) => void;

  liveSpeechMode: string | null;
  setLiveSpeechMode: (value: string | null) => void;

  realtimePersona: PersonaMenuItem | null;
  setRealtimePersona: (value: PersonaMenuItem | null) => void;

  realtimeMaturityLevel: string | null;
  setRealtimeMaturityLevel: (value: string | null) => void;

  realtimeMenuOptions: Record<string, any>;
  setRealtimeMenuOptions: (value: Record<string, any>) => void;

  realtimeDetailsWindowText: string | null;
  setRealtimeDetailsWindowText: (value: string | null) => void;
  waitingForDetailsConfirmation: boolean;
  setWaitingForDetailsConfirmation: (value: boolean) => void;

  lessonDialogue: boolean;
  setLessonDialogue: (value: boolean) => void;

  lessonDialogueMetadata: unknown;
  setLessonDialogueMetadata: (value: unknown) => void;

  lessonTurnsAllowed: number | null;
  setLessonTurnsAllowed: (value: number | null) => void;
  lessonTurnsUsed: number;
  setLessonTurnsUsed: (value: number) => void;
  incrementLessonTurnsUsed: () => void;

  lessonSecondsAllowed: number | null;
  setLessonSecondsAllowed: (value: number | null) => void;
  lessonSecondsRemaining: number | null;
  setLessonSecondsRemaining: (value: number | null) => void;

  playSoundAnswerIsCorrect: boolean | null;
  setPlaySoundAnswerIsCorrect: (value: boolean | null) => Promise<void>;

  playSoundBadge: boolean;
  setPlaySoundBadge: (value: boolean) => Promise<void>;

  showLessonOnboarding: boolean;
  setShowLessonOnboarding: (value: boolean) => Promise<void>;

  refreshChat: boolean;
  setRefreshChat: (value: boolean) => Promise<void>;

  feedBackAnimationFinish: boolean;
  setFeedBackAnimationFinish: (value: boolean) => Promise<void>;

  shouldOpenUnreadLessons: boolean;
  triggerOpenUnreadLessons: () => void;
  clearOpenUnreadLessons: () => void;

  isPreassessmentOpen: boolean;
  setIsPreassessmentOpen: (value: boolean) => void;

  isAssessmentOpen: boolean;
  setIsAssessmentOpen: (value: boolean) => void;

  // Internet connection status
  isOnline: boolean;
  setIsOnline: (value: boolean) => void;
  showReloadBanner: boolean;
  setShowReloadBanner: (value: boolean) => void;

  showResumeDialogue: boolean;
  setShowResumeDialogue: (value: boolean) => void;

  isTabVisible: boolean;
  setIsTabVisible: (value: boolean) => void;

  startRecordingTrigger: number;
  triggerStartRecording: () => void;

  connectionError: string | null;
  setConnectionError: (message: string | null) => void;

  avatarRingsAnimationTrigger: number;
  triggerAvatarRingsAnimation: () => void;

  deviceConflictModalOpen: boolean;
  setDeviceConflictModalOpen: (value: boolean) => void;
  currentSocketId: string | null;
  setCurrentSocketId: (sid: string | null) => void;

  isUserMenuModalOpen: boolean;
  setIsUserMenuModalOpen: (value: boolean) => void;

  responseContext: ResponseContextState;
  responseContextMenu: ResponseContextMenuResponse | null;
  fetchResponseContextMenu: () => Promise<ResponseContextMenuResponse>;
  // Pass changed ids; store builds full object and POSTs it entirely.
  setResponseContext: (data: {
    persona_id?: string | null;
    engagement_level_id?: string | null;
  }) => Promise<ResponseContextState>;
}

export const useAppStore = create<AppStore>((set, get) => ({
  showAttachmentsButton: readCachedAppConfig()?.showAttachmentsButton ?? false,
  markedIds: {},
  colorTheme: readCachedAppConfig()?.colorTheme ?? "light",
  messageTheme: getStoredMessageTheme(),
  isDark: readCachedAppConfig()?.colorTheme === "dark",
  focus: readCachedAppConfig()?.focus ?? "",
  domain: readCachedAppConfig()?.domain ?? "",
  originalFocus: readCachedAppConfig()?.focus ?? null,
  originalDomain: readCachedAppConfig()?.domain ?? null,
  botAvatar: (readCachedAppConfig()?.botAvatar ?? "") as AppStore["botAvatar"],
  userAvatar: readCachedAppConfig()?.userAvatar ?? "",
  fetchConfig: async () => {
    try {
      const prev = get();
      const config = await getUserConfig();
      const theme = config.color_theme;
      const storedTheme = getStoredMessageTheme();
      // Preserve MMK from localStorage (API may not return it yet)
      const messageTheme =
        storedTheme === "mmk" ? "mmk" : config.lesson_chat_mode;
      writeCachedAppConfig({
        focus: config.focus,
        domain: config.domain,
        colorTheme: theme,
        botAvatar: config.bot_avatar,
        userAvatar: config.user_avatar,
        messageTheme,
        showAttachmentsButton: config.show_attachments_button,
      });
      const focusOrDomainChanged =
        config.focus !== prev.focus || config.domain !== prev.domain;
      // Stale focus/domain from a previous session: reload chat after config corrects them.
      // Empty → filled is handled by useInitialMessages waiting on focus/domain.
      const shouldRefreshChat =
        focusOrDomainChanged && Boolean(prev.focus || prev.domain);
      const updatedState: Partial<AppStore> = {
        showAttachmentsButton: config.show_attachments_button,
        markedIds: config.marked_ids,
        colorTheme: theme,
        isDark: theme === "dark",
        focus: config.focus,
        domain: config.domain,
        originalFocus: config.focus,
        originalDomain: config.domain,
        botAvatar: config.bot_avatar,
        userAvatar: config.user_avatar,
        messageTheme,
        ...(shouldRefreshChat ? { refreshChat: true } : {}),
      };
      if (config.response_context) {
        updatedState.responseContext = buildResponseContextState(
          {
            persona_id: config.response_context.persona_id ?? null,
            engagement_level_id:
              config.response_context.engagement_level_id ?? null,
          },
          config.response_context,
          get().responseContextMenu,
        );
      } else {
        updatedState.responseContext = {
          persona_id: null,
          engagement_level_id: null,
          persona: null,
          engagement_level: null,
          is_default: true,
          display_name: null,
        };
      }
      set(updatedState);
      clearMessageMenuCache();
    } catch (error) {
      console.error("Failed to fetch config:", error);
      const cached = readCachedAppConfig();
      if (cached) {
        set({
          showAttachmentsButton: cached.showAttachmentsButton,
          colorTheme: cached.colorTheme,
          isDark: cached.colorTheme === "dark",
          focus: cached.focus,
          domain: cached.domain,
          originalFocus: cached.focus,
          originalDomain: cached.domain,
          botAvatar: cached.botAvatar as AppStore["botAvatar"],
          userAvatar: cached.userAvatar,
          messageTheme: cached.messageTheme,
        });
      }
    }
  },
  updateColorTheme: async (newTheme: "light" | "dark") => {
    set({ colorTheme: newTheme, isDark: newTheme === "dark" });
    try {
      await changeColorTheme(newTheme);
    } catch (error) {
      console.error("Failed to update color theme:", error);
    }
  },

  updateMessageTheme: async (
    newMessage: "both" | "lessons" | "chats" | "mmk",
  ) => {
    const currentState = get();
    const { persistMessagesToCacheFor } = useChatStore.getState();
    await persistMessagesToCacheFor(
      currentState.domain,
      currentState.focus,
      currentState.messageTheme,
    );
    set({ messageTheme: newMessage });
    try {
      localStorage.setItem(MESSAGE_THEME_STORAGE_KEY, newMessage);
    } catch {
      // ignore
    }
    // Skip API call for MMK until backend supports it
    if (newMessage === "mmk") {
      return "";
    }
    // Offline: keep local state/theme only, avoid network errors.
    if (!navigator.onLine) {
      return "";
    }
    try {
      const response = await changeMessageTheme(newMessage);
      return response;
    } catch (error) {
      console.error("Failed to update message theme:", error);
      return "";
    }
  },

  setIsDark: (value: boolean) => {
    set({ isDark: value, colorTheme: value ? "dark" : "light" });
  },

  setDomainFocus: async (
    domain: string,
    focus: string,
    storeOriginal = false,
  ) => {
    if (get().isAssessmentOpen) {
      console.warn(
        "[AppStore] Ignoring setDomainFocus while assessment is open",
        { domain, focus },
      );
      return;
    }

    const currentState = get();
    if (currentState.domain === domain && currentState.focus === focus) {
      return;
    }

    const { clearChatInfoData } = useChatStore.getState();

    if (storeOriginal) {
      if (
        currentState.originalDomain === null ||
        currentState.originalFocus === null
      ) {
        set({
          originalDomain: currentState.domain || "",
          originalFocus: currentState.focus || "",
        });
      }
    }

    const { persistMessagesToCacheFor } = useChatStore.getState();
    await persistMessagesToCacheFor(
      currentState.domain,
      currentState.focus,
      currentState.messageTheme,
    );

    if (navigator.onLine) {
      await persistDomainFocus(domain, focus);
    }

    patchCachedDomainFocus(domain, focus, currentState);
    // Personas/response_context are per focus — reset UI immediately, then reload.
    set({
      domain,
      focus,
      refreshChat: true,
      responseContextMenu: null,
      responseContext: {
        persona_id: null,
        engagement_level_id: null,
        persona: null,
        engagement_level: null,
        is_default: true,
        display_name: null,
      },
    });
    clearMessageMenuCache();
    await clearChatInfoData();

    if (navigator.onLine) {
      await get().fetchConfig();
      try {
        await get().fetchResponseContextMenu();
      } catch (error) {
        console.error(
          "Failed to refresh response context menu after focus change:",
          error,
        );
      }
    }
  },

  restoreOriginalDomainFocus: async () => {
    const currentState = get();

    if (currentState.isAssessmentOpen) {
      console.warn(
        "[AppStore] Ignoring restoreOriginalDomainFocus while assessment is open",
      );
      return;
    }

    if (
      currentState.originalDomain !== null &&
      currentState.originalFocus !== null
    ) {
      await get().setDomainFocus(
        currentState.originalDomain,
        currentState.originalFocus,
      );
    } else {
      console.warn(
        "[AppStore] Unable to restore domain and focus: original values not saved",
      );
    }
  },

  setDomain: (domain: string) => {
    if (get().isAssessmentOpen) return;
    set({ domain });
    clearMessageMenuCache();
  },

  setFocus: (focus: string) => {
    if (get().isAssessmentOpen) return;
    set({ focus });
    clearMessageMenuCache();
  },

  updateBotAvatar: async (
    newAvatar: "Alice" | "Endy" | "Mario" | "Robbi" | "Fatima" | "",
  ) => {
    try {
      await setBotAvatar(newAvatar);
      set({ botAvatar: newAvatar });
    } catch (error) {
      console.error("Failed to update bot avatar:", error);
    }
  },
  uploadUserAvatar: async (file: File) => {
    try {
      const avatarUrl = await uploadUserAvatar(file);
      set({ userAvatar: avatarUrl });
    } catch (error) {
      console.error("Failed to upload user avatar:", error);
    }
  },
  isBrowserLocked: false,
  browserLockedMessage: null,
  isAudioLoading: false,
  setIsBrowserLocked: async (value: boolean, playSound: boolean = true) => {
    if (get().isBrowserLocked !== value) {
      //No play sound if TTS audio is loading
      if (playSound && !get().isAudioLoading) {
        playActionSound();
      }
    }
    set({ isBrowserLocked: value });
  },
  setBrowserLockedMessage: (message: string | null) =>
    set({ browserLockedMessage: message }),
  setIsAudioLoading: (value: boolean) => {
    set({ isAudioLoading: value });
  },

  shouldHideStopMessage: false,
  setShouldHideStopMessage: (value: boolean) =>
    set({ shouldHideStopMessage: value }),

  sellingDialogue: false,
  setSellingDialogue: (value: boolean) => set({ sellingDialogue: value }),

  asyncRoleplayOpen: false,
  setAsyncRoleplayOpen: (value: boolean) => set({ asyncRoleplayOpen: value }),
  asyncRoleplaySessionId: null,
  setAsyncRoleplaySessionId: (value: string | null) =>
    set({ asyncRoleplaySessionId: value }),
  asyncRoleplayRecordId: null,
  setAsyncRoleplayRecordId: (value: string | null) =>
    set({ asyncRoleplayRecordId: value }),

  liveSpeechMode: null,
  setLiveSpeechMode: (value: string | null) => set({ liveSpeechMode: value }),

  realtimePersona: null,
  setRealtimePersona: (value: PersonaMenuItem | null) =>
    set({ realtimePersona: value }),

  realtimeMaturityLevel: null,
  setRealtimeMaturityLevel: (value: string | null) =>
    set({ realtimeMaturityLevel: value }),

  realtimeMenuOptions: {},
  setRealtimeMenuOptions: (value: Record<string, any>) =>
    set({ realtimeMenuOptions: value }),

  realtimeDetailsWindowText: null,
  setRealtimeDetailsWindowText: (value: string | null) =>
    set({ realtimeDetailsWindowText: value }),
  waitingForDetailsConfirmation: false,
  setWaitingForDetailsConfirmation: (value: boolean) =>
    set({ waitingForDetailsConfirmation: value }),

  lessonDialogue: false,
  setLessonDialogue: (value: boolean) => set({ lessonDialogue: value }),

  lessonDialogueMetadata: null,
  setLessonDialogueMetadata: (value: unknown) =>
    set({ lessonDialogueMetadata: value }),

  lessonTurnsAllowed: null,
  setLessonTurnsAllowed: (value: number | null) =>
    set({ lessonTurnsAllowed: value }),
  lessonTurnsUsed: 0,
  setLessonTurnsUsed: (value: number) => set({ lessonTurnsUsed: value }),
  incrementLessonTurnsUsed: () =>
    set((state) => ({ lessonTurnsUsed: state.lessonTurnsUsed + 1 })),

  lessonSecondsAllowed: null,
  setLessonSecondsAllowed: (value: number | null) =>
    set({ lessonSecondsAllowed: value }),
  lessonSecondsRemaining: null,
  setLessonSecondsRemaining: (value: number | null) =>
    set({ lessonSecondsRemaining: value }),

  playSoundAnswerIsCorrect: null,
  setPlaySoundAnswerIsCorrect: async (value: boolean | null) => {
    set({ playSoundAnswerIsCorrect: value });
  },

  playSoundBadge: false,
  setPlaySoundBadge: async (value: boolean) => {
    set({ playSoundBadge: value });
  },

  showLessonOnboarding: false,
  setShowLessonOnboarding: async (value: boolean) => {
    const { isAssessmentOpen, isPreassessmentOpen } = get();
    if (value && (isAssessmentOpen || isPreassessmentOpen)) {
      return;
    }
    set({ showLessonOnboarding: value });
  },

  refreshChat: false,
  setRefreshChat: async (value: boolean) => {
    set({ refreshChat: value });
  },

  feedBackAnimationFinish: false,
  setFeedBackAnimationFinish: async (value: boolean) => {
    set({ feedBackAnimationFinish: value });
  },

  shouldOpenUnreadLessons: false,
  triggerOpenUnreadLessons: () => set({ shouldOpenUnreadLessons: true }),
  clearOpenUnreadLessons: () => set({ shouldOpenUnreadLessons: false }),

  isPreassessmentOpen: false,
  setIsPreassessmentOpen: (value: boolean) =>
    set(
      value
        ? { isPreassessmentOpen: true, showLessonOnboarding: false }
        : { isPreassessmentOpen: false },
    ),

  isAssessmentOpen: false,
  setIsAssessmentOpen: (value: boolean) =>
    set(
      value
        ? { isAssessmentOpen: true, showLessonOnboarding: false }
        : { isAssessmentOpen: false },
    ),

  // Internet connection status
  isOnline: navigator.onLine,
  setIsOnline: (value: boolean) => set({ isOnline: value }),
  showReloadBanner: false,
  setShowReloadBanner: (value: boolean) => set({ showReloadBanner: value }),

  showResumeDialogue: false,
  setShowResumeDialogue: (value: boolean) => set({ showResumeDialogue: value }),

  isTabVisible: true,
  setIsTabVisible: (value: boolean) => set({ isTabVisible: value }),

  startRecordingTrigger: 0,
  triggerStartRecording: () =>
    set((state) => ({
      startRecordingTrigger: state.startRecordingTrigger + 1,
    })),

  connectionError: null,
  setConnectionError: (message: string | null) =>
    set({ connectionError: message }),

  avatarRingsAnimationTrigger: 0,
  triggerAvatarRingsAnimation: () =>
    set((state) => ({
      avatarRingsAnimationTrigger: state.avatarRingsAnimationTrigger + 1,
    })),

  deviceConflictModalOpen: false,
  setDeviceConflictModalOpen: (value) =>
    set({ deviceConflictModalOpen: value }),
  currentSocketId: null,
  setCurrentSocketId: (sid) => set({ currentSocketId: sid }),

  isUserMenuModalOpen: false,
  setIsUserMenuModalOpen: (value) => set({ isUserMenuModalOpen: value }),

  responseContext: {
    persona_id: null,
    engagement_level_id: null,
    persona: null,
    engagement_level: null,
    is_default: true,
    display_name: null,
  },
  responseContextMenu: null,
  fetchResponseContextMenu: async () => {
    const cached = get().responseContextMenu;
    if (cached) return cached;

    const menu = await getResponseContextMenu();
    const { responseContext } = get();
    set({
      responseContextMenu: menu,
      responseContext: buildResponseContextState(
        {
          persona_id: responseContext.persona_id,
          engagement_level_id: responseContext.engagement_level_id,
        },
        responseContext,
        menu,
      ),
    });
    return menu;
  },
  setResponseContext: async (data) => {
    const { responseContext, responseContextMenu } = get();
    const menu =
      responseContextMenu ?? (await get().fetchResponseContextMenu());

    // Always build and send the FULL object (backend stores/echoes as-is).
    const fullState = buildResponseContextState(data, responseContext, menu);
    set({ responseContext: fullState });

    try {
      const result = await apiSetResponseContext(fullState);
      if (isResponseContextState(result)) {
        const normalized = buildResponseContextState(
          {
            persona_id: result.persona_id,
            engagement_level_id: result.engagement_level_id,
          },
          result,
          menu,
        );
        set({ responseContext: normalized });
        return normalized;
      }

      // Backend echoed partial payload — keep our full local state
      return fullState;
    } catch (error) {
      set({ responseContext });
      throw error;
    }
  },
}));

export default useAppStore;
